import {
  MedicalRecordItem,
  useLazyGetCaseProvidersQuery,
  useLazyGetCasesBasicQuery,
  useLazyGetDocumentGetDownloadUnassignedQuery,
  useLazyGetMedicalRecordsFiltersQuery,
  useLazyGetMedicalRecordsQuery,
  useLazyPostDocumentAssignCaseQuery,
  useLazyPostDocumentDeleteUnassignedQuery,
  useLazyPostDocumentProviderAddNewUnassignedQuery,
  useLazyPostDocumentProviderAddUnassignedQuery,
  useLazyGetGdprSummaryQuery,
  GdprSummaryResponse,
  isEndpointLockError,
} from '@/api';
import { useAzureFileUpload } from '@repo/file-uploader';
import { BROADCAST_GDPR_CHECK_STARTED_FROM_CASE } from '@/broadcast/constants';
import {
  selectMedicalRecordsFilters,
  selectMedicalRecordsFinding,
  selectMedicalRecordsList,
  selectMedicalRecordsPagination,
  selectMedicalRecordsRowCount,
  selectMedicalRecordsSorting,
  selectMedicalRecordsSortingTouched,
  selectMedicalRecordsUploadingDocuments,
  selectSelectedMedicalRecordsFilters,
  selectSelectedMedicalRecordsUploadDateRange,
  selectCasesBasicList,
  selectCaseProvidersList,
  setMedicalRecordsPagination,
  setMedicalRecordsSorting,
  setSelectedMedicalRecordsFilters,
  setMedicalRecordsFinding,
  setSelectedMedicalRecordsUploadDateRange,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import {
  DownloadIcon,
  DataTableProgressBarRow,
  IndeterminateCheckbox,
  useGlobalToast,
  PdfIcon,
  UserAvatar,
  ProviderAvatar,
  CaseAvatar,
  PencilIcon,
  ProgressSpinner,
  YellowWarningIcon,
} from '@repo/atomic-components';
import { useConfirmDocumentDelete, useStartGdprCheck } from '@/hooks';
import { openNewTab, DocumentStatus, isDocumentDeleteBlockedByCaseStatus } from '@/utils';
import { ColumnDef, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { format } from 'date-fns';
import { classNames } from 'primereact/utils';
import type { ToastMessage } from 'primereact/toast';
import { useCallback, useEffect, useMemo, useState, useRef, FC } from 'react';
import { createPortal } from 'react-dom';
import {
  SORTING_COLUMN_UPLOADED_BY,
  ORDER_BY_CHECK_DATE,
  ORDER_BY_DATE,
  ORDER_BY_DEFAULT,
  MEDICAL_RECORDS_DELETE_DOCUMENT,
  DOCUMENT_PROCESSING_STATUSES,
  DOCUMENT_ERROR_STATUSES,
  AppRoutes,
} from '@/constants';
import { DeleteDocumentButton } from '@/ui/components/DeleteDocumentButton';
import { useUrlSync } from './useUrlSync';

export interface MedicalRecordsListTableRow extends MedicalRecordItem {
  progressBars?: DataTableProgressBarRow[];
}

const normalizeUploadDateRange = (minUploadDate?: string, maxUploadDate?: string) => {
  const normalizeDate = (value?: string) => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;

    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
      ? value
      : undefined;
  };

  const normalizedMinDate = normalizeDate(minUploadDate);
  const normalizedMaxDate = normalizeDate(maxUploadDate);

  if (normalizedMinDate && normalizedMaxDate && normalizedMinDate > normalizedMaxDate) return {};

  return { minUploadDate: normalizedMinDate, maxUploadDate: normalizedMaxDate };
};

// Extracted cell components to avoid remounting on every render

interface CaseCellProps {
  row: MedicalRecordsListTableRow;
  rowId: string;
  caseStatus: string;
  onSelectCaseClick: (e: React.MouseEvent<HTMLButtonElement>, rowId: string) => void;
}

const CaseCell: FC<CaseCellProps> = ({ row, rowId, caseStatus, onSelectCaseClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  if (row.unassignedDocumentId) {
    return (
      <button
        type="button"
        onClick={(e) => onSelectCaseClick(e, rowId)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="group flex cursor-pointer items-center gap-2"
      >
        {isHovered ? (
          <div className="bg-fletchers-gray-110 flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
            <PencilIcon className="text-fletchers-black-900 h-3 w-3" />
          </div>
        ) : (
          <CaseAvatar unassigned size={24} />
        )}
        <span
          className={classNames(
            'italic transition-colors',
            isHovered ? 'text-fletchers-black-900' : 'text-fletchers-gray-200',
          )}
        >
          Select case
        </span>
      </button>
    );
  }

  const displayCaseName = row.caseName.length > 18 ? `${row.caseName.slice(0, 18)}...` : row.caseName;

  return (
    <div
      className="flex cursor-pointer items-center gap-2"
      onClick={() => row.caseId && openNewTab(`/case-list/${row.caseId}`)}
    >
      <CaseAvatar name={row.caseName} size={24} />
      <div className="flex flex-col items-start">
        <span
          className="text-fletchers-black-900 hover:text-fletchers-green-500 block max-w-[400px] overflow-hidden text-[14px]/[20px] font-semibold overflow-ellipsis whitespace-nowrap transition-colors"
          title={row.caseName}
        >
          {displayCaseName}
        </span>
        {caseStatus && (
          <span className="text-fletchers-gray-250 text-[12px]/[16px] whitespace-nowrap">{caseStatus}</span>
        )}
      </div>
    </div>
  );
};

interface ProviderCellProps {
  rowId: string;
  providerName: string;
  providerType: string;
  onSelectProviderClick: (e: React.MouseEvent<HTMLButtonElement>, rowId: string) => void;
}

const ProviderCell: FC<ProviderCellProps> = ({ rowId, providerName, providerType, onSelectProviderClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  if (!providerName) {
    return (
      <button
        type="button"
        onClick={(e) => onSelectProviderClick(e, rowId)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="group flex cursor-pointer items-center gap-2"
      >
        {isHovered ? (
          <div className="bg-fletchers-gray-110 flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
            <PencilIcon className="text-fletchers-black-900 h-3 w-3" />
          </div>
        ) : (
          <ProviderAvatar unassigned size={24} />
        )}
        <span
          className={classNames(
            'italic transition-colors',
            isHovered ? 'text-fletchers-black-900' : 'text-fletchers-gray-200',
          )}
        >
          Select provider
        </span>
      </button>
    );
  }

  const displayName = providerName.length > 32 ? `${providerName.slice(0, 32)}...` : providerName;

  return (
    <button
      type="button"
      onClick={(e) => onSelectProviderClick(e, rowId)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group flex cursor-pointer items-center gap-2"
    >
      {isHovered ? (
        <div className="bg-fletchers-gray-110 flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
          <PencilIcon className="text-fletchers-black-900 h-3 w-3" />
        </div>
      ) : (
        <ProviderAvatar name={providerName} size={24} />
      )}
      <div className="flex flex-col items-start">
        <span
          className={classNames(
            'block max-w-[400px] overflow-hidden overflow-ellipsis whitespace-nowrap transition-colors',
            isHovered ? 'text-fletchers-black-900' : '',
          )}
          title={providerName}
        >
          {displayName}
        </span>
        {providerType && (
          <span className="text-fletchers-gray-250 text-[12px]/[16px] whitespace-nowrap">{providerType}</span>
        )}
      </div>
    </button>
  );
};

interface StatusCellProps {
  formattedStatus: string;
  failureReason: string | null;
}

const StatusCell: FC<StatusCellProps> = ({ formattedStatus, failureReason }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setShowTooltip(true);

    const rect = e.currentTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    const estimatedTooltipHeight = 80;

    if (spaceBelow < estimatedTooltipHeight && spaceAbove > spaceBelow) {
      setTooltipPosition({
        top: rect.top - estimatedTooltipHeight - 8,
        left: rect.left,
      });
    } else {
      setTooltipPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, []);

  return (
    <>
      <div
        className="relative inline-flex cursor-pointer items-center gap-2"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <YellowWarningIcon style={{ width: '16px', height: '16px' }} />
        <span className="text-fletchers-black-900 font-semibold">{formattedStatus}</span>
      </div>
      {showTooltip &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[999999] w-max max-w-sm rounded-xl border border-gray-100 bg-white px-4 py-3 text-sm text-black shadow-xl"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
          >
            {failureReason}
          </div>,
          document.body,
        )}
    </>
  );
};

export const useMedicalRecordsListTable = () => {
  const dispatch = useAppDispatch();
  const globalToastRef = useGlobalToast();
  const confirmDocumentDelete = useConfirmDocumentDelete();
  const { getAllParams, updateMultipleParams, removeMultipleParams, searchParams } = useUrlSync();
  const isUrlSyncRef = useRef<boolean>(false);

  const [rowSelection, setRowSelection] = useState({});

  const medicalRecordsUploadingDocuments = useAppSelector(selectMedicalRecordsUploadingDocuments);

  const { uploadList, downloadFile /*, uploadFile, deleteFile, deleteUpload */ } = useAzureFileUpload();

  const medicalRecordsList = useAppSelector(selectMedicalRecordsList);
  const medicalRecordsRowCount = useAppSelector(selectMedicalRecordsRowCount);
  const medicalRecordsPagination = useAppSelector(selectMedicalRecordsPagination);
  const medicalRecordsSorting = useAppSelector(selectMedicalRecordsSorting);
  const medicalRecordsSortingTouched = useAppSelector(selectMedicalRecordsSortingTouched);
  const medicalRecordsFinding = useAppSelector(selectMedicalRecordsFinding);
  const medicalRecordsFilters = useAppSelector(selectMedicalRecordsFilters);
  const selectedMedicalRecordsFilters = useAppSelector(selectSelectedMedicalRecordsFilters);
  const selectedUploadDateRange = useAppSelector(selectSelectedMedicalRecordsUploadDateRange);
  const casesBasicList = useAppSelector(selectCasesBasicList);
  const caseProvidersList = useAppSelector(selectCaseProvidersList);

  const [triggerGetMedicalRecords, { isFetching: getMedicalRecordsFetching, currentData: medicalRecordsCurrentData }] =
    useLazyGetMedicalRecordsQuery();
  const [triggerGetMedicalFiltersRecords, { isSuccess: getMedicalRecordsFiltersSuccess }] =
    useLazyGetMedicalRecordsFiltersQuery();
  const [triggerGetDocumentGetDownloadUnassigned] = useLazyGetDocumentGetDownloadUnassignedQuery();
  const [triggerPostDocumentDeleteUnassigned] = useLazyPostDocumentDeleteUnassignedQuery();
  const [triggerPostDocumentProviderAddUnassigned] = useLazyPostDocumentProviderAddUnassignedQuery();
  const [triggerPostDocumentProviderAddNewUnassigned] = useLazyPostDocumentProviderAddNewUnassignedQuery();
  const [triggerPostDocumentAssignCase] = useLazyPostDocumentAssignCaseQuery();

  const { startGdprCheck, startingGdprCheckDocumentIds } = useStartGdprCheck();
  const [triggerGetGdprSummary] = useLazyGetGdprSummaryQuery();

  const [triggerGetCaseProviders] = useLazyGetCaseProvidersQuery();
  const [triggerGetCasesBasic] = useLazyGetCasesBasicQuery();

  // No local row edit state, all changes go to API
  const [data, setData] = useState<MedicalRecordsListTableRow[]>([]);

  // State for GDPR Check Summary Modal
  const [gdprSummaryVisible, setGdprSummaryVisible] = useState(false);
  const [gdprSummaryData, setGdprSummaryData] = useState<GdprSummaryResponse | null>(null);
  const [gdprSummaryLoading, setGdprSummaryLoading] = useState(false);

  // State for Case Selector Popup
  const [caseSelectorVisible, setCaseSelectorVisible] = useState(false);
  const [caseSelectorPosition, setCaseSelectorPosition] = useState({ top: 0, left: 0 });
  const [caseSelectorPendingRows, setCaseSelectorPendingRows] = useState<string[]>([]);

  // State for Provider Selector Popup
  const [providerSelectorVisible, setProviderSelectorVisible] = useState(false);
  const [providerSelectorPosition, setProviderSelectorPosition] = useState({ top: 0, left: 0 });
  const [providerSelectorPendingRows, setProviderSelectorPendingRows] = useState<string[]>([]);

  useEffect(() => {
    triggerGetMedicalFiltersRecords({});
    triggerGetCaseProviders({});
    triggerGetCasesBasic({});
    return () => {
      // Reset the search term when leaving the page so a stale query from
      // this session doesn't silently filter the list on next visit.
      dispatch(setMedicalRecordsFinding(''));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Separate effect to handle page parameter from URL (e.g., when returning from GDPR check)
  useEffect(() => {
    const pageParam = searchParams.get('page');
    if (pageParam) {
      const pageIndex = parseInt(pageParam, 10);
      if (!isNaN(pageIndex) && pageIndex >= 0) {
        dispatch(
          setMedicalRecordsPagination({
            ...medicalRecordsPagination,
            pageIndex,
          }),
        );
        // Remove the page parameter from URL after restoring it (like broadcast scroll-to-page behavior)
        removeMultipleParams(['page']);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (getMedicalRecordsFiltersSuccess) {
      const params = getAllParams();
      const updatedFilters = { ...selectedMedicalRecordsFilters };

      if (params.nameFilter) {
        dispatch(setMedicalRecordsFinding(params.nameFilter));
      }

      if (params.caseStatus) {
        updatedFilters.caseStatus = medicalRecordsFilters.caseStatus.filter((fi) =>
          params.caseStatus.split(',').includes(fi.raw),
        );
      }
      if (params.checkedBy) {
        updatedFilters.checkedBy = medicalRecordsFilters.checkedBy.filter((fi) =>
          params.checkedBy.split(',').includes(fi.userId),
        );
      }
      if (params.documentStatus) {
        updatedFilters.documentStatus = medicalRecordsFilters.documentStatus.filter((fi) =>
          params.documentStatus.split(',').includes(fi.raw),
        );
      }
      if (params.providerTypes) {
        updatedFilters.providerTypes = medicalRecordsFilters.providerTypes.filter((fi) =>
          params.providerTypes.split(',').includes(fi.raw),
        );
      }
      if (params.providerId) {
        updatedFilters.providers = medicalRecordsFilters.providers.filter((fi) =>
          params.providerId.split(',').includes(fi ? fi.providerId : 'null'),
        );
      }
      if (params.uploadedBy) {
        updatedFilters.uploadedBy = medicalRecordsFilters.uploadedBy.filter((fi) =>
          params.uploadedBy.split(',').includes(fi.userId),
        );
      }

      dispatch(setSelectedMedicalRecordsFilters(updatedFilters));
      dispatch(
        setSelectedMedicalRecordsUploadDateRange(normalizeUploadDateRange(params.minUploadDate, params.maxUploadDate)),
      );
      isUrlSyncRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMedicalRecordsFiltersSuccess]);

  const getMedicalRecordsListRequest = useCallback(() => {
    let orderBy: string | undefined = ORDER_BY_DEFAULT;
    let orderDirection: string | undefined = 'descending';
    if (medicalRecordsSortingTouched && medicalRecordsSorting && medicalRecordsSorting.length > 0) {
      orderBy = medicalRecordsSorting[0].id === SORTING_COLUMN_UPLOADED_BY ? ORDER_BY_DATE : ORDER_BY_CHECK_DATE;
      orderDirection = medicalRecordsSorting[0].desc ? 'descending' : 'ascending';
    }

    const queryParams = {
      ...medicalRecordsPagination, // {pageIndex: 0, pageSize: 10}
      orderBy,
      orderDirection,
      caseStatus: selectedMedicalRecordsFilters.caseStatus.map((fi) => fi.raw) || [],
      checkedBy: selectedMedicalRecordsFilters.checkedBy.map((fi) => fi.userId) || [],
      documentStatus: selectedMedicalRecordsFilters.documentStatus.map((s) => s.raw) || [],
      providerType: selectedMedicalRecordsFilters.providerTypes.map((fi) => fi.raw) || [],
      providerId: selectedMedicalRecordsFilters.providers.map((fi) => (fi ? fi.providerId : 'null')) || [],
      uploadedBy: selectedMedicalRecordsFilters.uploadedBy.map((fi) => fi.userId) || [],
      minUploadDate: selectedUploadDateRange.minUploadDate,
      maxUploadDate: selectedUploadDateRange.maxUploadDate,
      nameFilter: medicalRecordsFinding.length ? medicalRecordsFinding : undefined,
    };

    // Only update URL params after URL sync is complete to avoid
    // clearing URL params before they have been read.
    if (isUrlSyncRef.current) {
      updateMultipleParams({
        nameFilter: queryParams.nameFilter?.length ? queryParams.nameFilter : undefined,
        caseStatus: queryParams.caseStatus.length ? queryParams.caseStatus.join(',') : undefined,
        checkedBy: queryParams.checkedBy.length ? queryParams.checkedBy.join(',') : undefined,
        documentStatus: queryParams.documentStatus.length ? queryParams.documentStatus.join(',') : undefined,
        providerTypes: queryParams.providerType.length ? queryParams.providerType.join(',') : undefined,
        providerId: queryParams.providerId.length ? queryParams.providerId.join(',') : undefined,
        uploadedBy: queryParams.uploadedBy.length ? queryParams.uploadedBy.join(',') : undefined,
        minUploadDate: queryParams.minUploadDate,
        maxUploadDate: queryParams.maxUploadDate,
      });
    }

    return triggerGetMedicalRecords(queryParams, false);
  }, [
    medicalRecordsSorting,
    medicalRecordsSortingTouched,
    medicalRecordsPagination,
    selectedMedicalRecordsFilters.caseStatus,
    selectedMedicalRecordsFilters.checkedBy,
    selectedMedicalRecordsFilters.documentStatus,
    selectedMedicalRecordsFilters.providerTypes,
    selectedMedicalRecordsFilters.providers,
    selectedMedicalRecordsFilters.uploadedBy,
    selectedUploadDateRange.minUploadDate,
    selectedUploadDateRange.maxUploadDate,
    medicalRecordsFinding,
    updateMultipleParams,
    triggerGetMedicalRecords,
  ]);

  useEffect(() => {
    // Auto-refresh loop. The `isCancelled` flag + tracking the timeout in a
    // scoped variable are both necessary — the naive `let timeoutId + return
    // clearTimeout(timeoutId)` version leaks a runaway timer if the user
    // navigates away while `getMedicalRecordsListRequest` is still awaiting.
    // In that race, cleanup runs while `timeoutId` is undefined, then the
    // await resolves and schedules a new setTimeout that no cleanup will
    // ever cancel — it fires 5 minutes later on whichever page the user is
    // now on and triggers a stray URL update on the wrong pathname.
    let isCancelled = false;
    let timeoutId: NodeJS.Timeout | undefined;
    const fetchData = async () => {
      await getMedicalRecordsListRequest();
      if (isCancelled) return;
      timeoutId = setTimeout(fetchData, 5 * 60 * 1000); // 5 min
    };

    fetchData();
    return () => {
      isCancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [getMedicalRecordsListRequest]);

  // Listen for GDPR check started from case page
  useEffect(() => {
    const handleGdprCheckStartedFromCase = () => {
      getMedicalRecordsListRequest();
    };

    window.addEventListener(BROADCAST_GDPR_CHECK_STARTED_FROM_CASE, handleGdprCheckStartedFromCase);

    return () => {
      window.removeEventListener(BROADCAST_GDPR_CHECK_STARTED_FROM_CASE, handleGdprCheckStartedFromCase);
    };
  }, [getMedicalRecordsListRequest]);

  // Format status with only first letter capitalized, but preserve GDPR, AI, OCR
  const formatStatus = useCallback((status: string) => {
    return status
      .toLowerCase()
      .replace(/\b(gdpr|ai|ocr)\b/gi, (match) => match.toUpperCase())
      .replace(/^./, (match) => match.toUpperCase());
  }, []);

  // Adapt filter options for DataTableHeaderFilter
  const filterOptions: Record<string, string[]> = {
    caseStatus: medicalRecordsFilters.caseStatus.map((fi) => fi.rendered),
    checkedBy: medicalRecordsFilters.checkedBy.map((fi) => fi.userName),
    documentStatus: medicalRecordsFilters.documentStatus.map((fi) => formatStatus(fi.rendered)),
    providerTypes: medicalRecordsFilters.providerTypes.map((fi) => fi.rendered),
    providers: medicalRecordsFilters.providers.map((fi) =>
      fi ? `${fi.providerName}   ${fi.providerType}` : 'Unassigned',
    ),
    uploadedBy: medicalRecordsFilters.uploadedBy.map((fi) => fi.userName),
  };

  // Selected filter values for DataTableHeaderFilter
  const selectedFilterOptions: Record<string, string[]> = {
    caseStatus: selectedMedicalRecordsFilters.caseStatus.map((fi) => fi.rendered),
    checkedBy: selectedMedicalRecordsFilters.checkedBy.map((fi) => fi.userName),
    documentStatus: selectedMedicalRecordsFilters.documentStatus.map((fi) => formatStatus(fi.rendered)),
    providerTypes: selectedMedicalRecordsFilters.providerTypes.map((fi) => fi.rendered),
    providers: selectedMedicalRecordsFilters.providers.map((fi) =>
      fi ? `${fi.providerName}   ${fi.providerType}` : 'Unassigned',
    ),
    uploadedBy: selectedMedicalRecordsFilters.uploadedBy.map((fi) => fi.userName),
  };

  // Convert selectedFilterOptions to ColumnFilter[] for TanStack Table
  const columnFilters = Object.entries(selectedFilterOptions)
    .filter(([, value]) => value.length > 0)
    .map(([id, value]) => ({ id, value }));

  // Handle filter change (called on Apply in DataTableHeaderFilter)
  const handleFilterChange = (columnId: string, values: string[]) => {
    // Update selected filters in store
    const updated = { ...selectedMedicalRecordsFilters };
    if (columnId === 'caseStatus') {
      updated.caseStatus = medicalRecordsFilters.caseStatus.filter((fi) => values.includes(fi.rendered));
    } else if (columnId === 'checkedBy') {
      updated.checkedBy = medicalRecordsFilters.checkedBy.filter((fi) => values.includes(fi.userName));
    } else if (columnId === 'documentStatus') {
      updated.documentStatus = medicalRecordsFilters.documentStatus.filter((fi) =>
        values.includes(formatStatus(fi.rendered)),
      );
    } else if (columnId === 'providerTypes') {
      updated.providerTypes = medicalRecordsFilters.providerTypes.filter((fi) => values.includes(fi.rendered));
    } else if (columnId === 'providers') {
      updated.providers = medicalRecordsFilters.providers.filter((fi) =>
        values.includes(fi ? `${fi.providerName}   ${fi.providerType}` : 'Unassigned'),
      );
    } else if (columnId === 'uploadedBy') {
      updated.uploadedBy = medicalRecordsFilters.uploadedBy.filter((fi) => values.includes(fi.userName));
    }

    dispatch(setSelectedMedicalRecordsFilters(updated));
    // Reset pagination to first page
    dispatch(
      setMedicalRecordsPagination({
        ...medicalRecordsPagination,
        pageIndex: 0,
      }),
    );
  };

  const handleUploadedByFilterChange = (
    values: string[],
    dateRange: { minUploadDate?: string; maxUploadDate?: string },
  ) => {
    dispatch(
      setSelectedMedicalRecordsFilters({
        ...selectedMedicalRecordsFilters,
        uploadedBy: medicalRecordsFilters.uploadedBy.filter((filter) => values.includes(filter.userName)),
      }),
    );
    dispatch(
      setSelectedMedicalRecordsUploadDateRange(
        normalizeUploadDateRange(dateRange.minUploadDate, dateRange.maxUploadDate),
      ),
    );
    dispatch(
      setMedicalRecordsPagination({
        ...medicalRecordsPagination,
        pageIndex: 0,
      }),
    );
  };

  /**
   * Update data and uploads file uploads
   */
  useEffect(() => {
    //** Create new immutable copy */
    const medicalRecordsListWithProgressBars = medicalRecordsList.map((medicalRecordItem) => {
      if (!medicalRecordItem.documentId && !medicalRecordItem.unassignedDocumentId) {
        //** Return document without changes if not unassignedDocumentId */
        return medicalRecordItem;
      }

      //** Check if document is in uploadList and in uploadingDocuments */
      const isUploading: boolean = medicalRecordsUploadingDocuments.some(
        (m) =>
          m.documentId === medicalRecordItem.documentId ||
          m.unassignedDocumentId === medicalRecordItem.unassignedDocumentId,
      );

      if (isUploading) {
        //** Find upload info in uploadList */
        const uploadInfo = uploadList.find(
          (azureFileUpload) =>
            azureFileUpload.id === `${medicalRecordItem.documentId}:${medicalRecordItem.unassignedDocumentId}`,
        );
        if (uploadInfo) {
          //** Update progressBars from uploadList */
          return {
            ...medicalRecordItem,
            progressBars: [{ id: uploadInfo.id, value: uploadInfo.progress }],
          };
        }

        //** If no progressBars in uploadList, return document without changes */
        return medicalRecordItem;
      } else {
        //** Return document without changes if not uploading */
        return medicalRecordItem;
      }
    });

    setData(medicalRecordsListWithProgressBars);
  }, [medicalRecordsList, medicalRecordsUploadingDocuments, uploadList]);

  const handleGdprStartReviewClick = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      const gdprStartReviewDocumentId = event.currentTarget.dataset.documentId;
      const checkStarted = event.currentTarget.dataset.checkStarted === 'true';
      if (!gdprStartReviewDocumentId) return;

      const fetchLatestDocument = async () => {
        const { medicalRecords } = await getMedicalRecordsListRequest().unwrap();
        return medicalRecords.find((record) => record.documentId === gdprStartReviewDocumentId);
      };

      if (await startGdprCheck(gdprStartReviewDocumentId, checkStarted, fetchLatestDocument)) {
        // Store current page index in URL to restore when coming back
        const currentPage = medicalRecordsPagination.pageIndex;
        openNewTab(
          `/${AppRoutes.GdprCheck}/${gdprStartReviewDocumentId}?from=medical-records&returnPage=${currentPage}`,
        );
      }
    },
    [startGdprCheck, getMedicalRecordsListRequest, medicalRecordsPagination],
  );

  const handleGdprCheckCompletedClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const documentId = event.currentTarget.dataset.gdprCheckCompletedDocumentId;

      if (documentId) {
        setGdprSummaryLoading(true);
        setGdprSummaryVisible(true);

        triggerGetGdprSummary({ documentId })
          .unwrap()
          .then((data) => {
            setGdprSummaryData(data);
          })
          .catch((error) => {
            console.error('Failed to fetch GDPR summary:', error);
            setGdprSummaryData(null);
          })
          .finally(() => {
            setGdprSummaryLoading(false);
          });
      }
    },
    [triggerGetGdprSummary],
  );

  const handleDownloadDocumentClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const downloadDocumentId = event.currentTarget.dataset.downloadDocumentId;
      const downloadUnassignedDocumentId = event.currentTarget.dataset.downloadUnassignedDocumentId;
      const downloadDocumentFileName = event.currentTarget.dataset.downloadDocumentFilename;

      if (downloadDocumentId || downloadUnassignedDocumentId) {
        triggerGetDocumentGetDownloadUnassigned({
          documentId: downloadDocumentId || null,
          unassignedDocumentId: downloadUnassignedDocumentId || null,
        })
          .unwrap()
          .then((data) => {
            if (downloadDocumentFileName && data && data.downloadPath && data.downloadToken) {
              downloadFile(downloadDocumentFileName, data.downloadPath, data.downloadToken);
            }
          });
      }
    },
    [downloadFile, triggerGetDocumentGetDownloadUnassigned],
  );

  const handleDeleteDocumentClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      const deleteDocumentId = event.currentTarget.dataset.deleteDocumentId;
      const deleteUnassignedDocumentId = event.currentTarget.dataset.deleteUnassignedDocumentId;

      if (deleteDocumentId || deleteUnassignedDocumentId) {
        const confirmed = await confirmDocumentDelete({
          dialogId: MEDICAL_RECORDS_DELETE_DOCUMENT,
          documentId: deleteDocumentId,
          unassignedDocumentId: deleteUnassignedDocumentId,
        });

        if (confirmed) {
          triggerPostDocumentDeleteUnassigned({
            documentId: deleteDocumentId || null,
            unassignedDocumentId: deleteUnassignedDocumentId || null,
          })
            .unwrap()
            .catch((error) => {
              console.error('Failed to delete document:', error);
              // The lock's own toast has already explained this one
              if (isEndpointLockError(error)) return;
              globalToastRef.current?.show({
                severity: 'error',
                summary: 'Delete failed',
                detail: 'Failed to delete the document. Please try again.',
                life: 5000,
              });
            });
        }
      }
    },
    [confirmDocumentDelete, triggerPostDocumentDeleteUnassigned, globalToastRef],
  );

  const handleSelectProviderClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, rowId: string) => {
      let selectedRows = Object.keys(table.getState().rowSelection);

      // Add row if not included
      if (!selectedRows.includes(rowId)) {
        selectedRows = [...selectedRows, rowId];
      }

      setProviderSelectorPendingRows(selectedRows);

      // Position the popup near the click
      const rect = event.currentTarget.getBoundingClientRect();
      const popoverWidth = 360;
      const popoverHeight = 400; // max height
      const pagePadding = 24;
      let left = rect.left;
      let top = rect.bottom + window.scrollY + 4;

      // Check if there's enough space below, otherwise position above
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < popoverHeight && rect.top > spaceBelow) {
        top = rect.top + window.scrollY - popoverHeight - 4;
      }

      // Ensure popup stays inside viewport horizontally
      if (rect.left + popoverWidth + pagePadding > window.innerWidth) {
        left = window.innerWidth - popoverWidth - pagePadding;
      }

      setProviderSelectorPosition({ top, left });
      setProviderSelectorVisible(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleProviderSelect = useCallback(
    (selectedProviderId: string, selectedProviderName: string, selectedProviderType: string) => {
      const selectedDocumentIds: string[] = [];
      const selectedUnassignedDocumentIds: string[] = [];

      providerSelectorPendingRows.forEach((selectedRowId) => {
        const selectedRowData = table.getRow(selectedRowId).original;
        if (selectedRowData.documentId) {
          selectedDocumentIds.push(selectedRowData.documentId);
        }
        if (selectedRowData.unassignedDocumentId) {
          selectedUnassignedDocumentIds.push(selectedRowData.unassignedDocumentId);
        }
      });

      if (!selectedDocumentIds.length && !selectedUnassignedDocumentIds.length) return;
      if (!selectedProviderId && !(selectedProviderName && selectedProviderType)) return;

      const updatingProvidersToast: ToastMessage = {
        severity: 'info',
        summary: 'Updating providers',
        detail: 'Please wait while providers are being assigned.',
        life: 10000,
      };
      globalToastRef.current?.show(updatingProvidersToast);

      if ((selectedDocumentIds.length || selectedUnassignedDocumentIds.length) && selectedProviderId) {
        triggerPostDocumentProviderAddUnassigned({
          providerId: selectedProviderId,
          documentIds: selectedDocumentIds,
          unassignedDocumentIds: selectedUnassignedDocumentIds,
        })
          .unwrap()
          .then(() => {
            getMedicalRecordsListRequest();
            globalToastRef.current?.clear();
            globalToastRef.current?.show({
              severity: 'success',
              summary: 'Providers updated',
              life: 5000,
            });
          })
          .catch((error) => {
            console.error('Provider assignment failed:', error);
            // Clearing would also take down the lock's own toast, which has already explained this one
            if (isEndpointLockError(error)) {
              globalToastRef.current?.remove(updatingProvidersToast);
              return;
            }
            globalToastRef.current?.clear();
            globalToastRef.current?.show({
              severity: 'error',
              summary: 'Provider update failed',
              detail: 'Please try again.',
              closable: true,
              life: 5000,
            });
          });

        table.resetRowSelection();
        return;
      }

      if (
        (selectedDocumentIds.length || selectedUnassignedDocumentIds.length) &&
        selectedProviderName &&
        selectedProviderType
      ) {
        triggerPostDocumentProviderAddNewUnassigned({
          providerName: selectedProviderName,
          providerType: selectedProviderType,
          documentIds: selectedDocumentIds,
          unassignedDocumentIds: selectedUnassignedDocumentIds,
        })
          .unwrap()
          .then(() => {
            getMedicalRecordsListRequest();
            triggerGetCaseProviders({});
            globalToastRef.current?.clear();
            globalToastRef.current?.show({
              severity: 'success',
              summary: 'Providers updated',
              life: 5000,
            });
          })
          .catch((error) => {
            console.error('Provider assignment failed:', error);
            if (isEndpointLockError(error)) {
              globalToastRef.current?.remove(updatingProvidersToast);
              return;
            }
            globalToastRef.current?.clear();
            globalToastRef.current?.show({
              severity: 'error',
              summary: 'Provider update failed',
              detail: 'Please try again.',
              closable: true,
              life: 5000,
            });
          });

        table.resetRowSelection();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      providerSelectorPendingRows,
      triggerPostDocumentProviderAddUnassigned,
      getMedicalRecordsListRequest,
      triggerPostDocumentProviderAddNewUnassigned,
      triggerGetCaseProviders,
      globalToastRef,
    ],
  );

  const handleSelectCaseClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, rowId: string) => {
      let selectedRows = Object.keys(table.getState().rowSelection);

      // Add row if not included
      if (!selectedRows.includes(rowId)) {
        selectedRows = [...selectedRows, rowId];
      }

      setCaseSelectorPendingRows(selectedRows);

      // Position the popup near the click
      const rect = event.currentTarget.getBoundingClientRect();
      const popoverWidth = 280;
      const popoverHeight = 400; // max height
      const pagePadding = 24;
      let left = rect.left;
      let top = rect.bottom + window.scrollY + 4;

      // Check if there's enough space below, otherwise position above
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < popoverHeight && rect.top > spaceBelow) {
        top = rect.top + window.scrollY - popoverHeight - 4;
      }

      // Ensure popup stays inside viewport horizontally
      if (rect.left + popoverWidth + pagePadding > window.innerWidth) {
        left = window.innerWidth - popoverWidth - pagePadding;
      }

      setCaseSelectorPosition({ top, left });
      setCaseSelectorVisible(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleCaseSelect = useCallback(
    (selectedCaseBasicId: string) => {
      const selectedUnassignedDocumentIds: string[] = [];

      caseSelectorPendingRows.forEach((selectedRowId) => {
        const selectedRowData = table.getRow(selectedRowId).original;

        if (selectedRowData.unassignedDocumentId) {
          selectedUnassignedDocumentIds.push(selectedRowData.unassignedDocumentId);
        }
      });

      if (selectedUnassignedDocumentIds.length && selectedCaseBasicId) {
        const promises = selectedUnassignedDocumentIds.map((unassignedDocumentId) => {
          return triggerPostDocumentAssignCase({
            unassignedDocumentId: unassignedDocumentId,
            caseId: selectedCaseBasicId,
          })
            .unwrap()
            .then((result) => ({
              status: 'fulfilled',
              value: result,
              unassignedDocumentId,
            }))
            .catch((error) => ({
              status: 'rejected',
              reason: error,
              unassignedDocumentId,
            }));
        });

        Promise.allSettled(promises).then((results) => {
          const failedAssignments = results.filter(
            (result) => result && 'value' in result && 'status' in result.value && result?.value?.status === 'rejected',
          );
          // Those refused for a lock have already been explained by the lock's own toast
          const hasNonLockFailures = failedAssignments.some(
            (assignment) =>
              !('value' in assignment && 'reason' in assignment.value && isEndpointLockError(assignment.value.reason)),
          );

          if (failedAssignments.length > 0) {
            console.groupCollapsed('The following documents failed to be assigned:');
            failedAssignments.forEach((assignment) => {
              console.error(
                `Document ID: ${assignment && 'value' in assignment && 'unassignedDocumentId' in assignment.value ? assignment?.value?.unassignedDocumentId : ' - '}, Error: ${assignment && 'value' in assignment && 'reason' in assignment.value ? assignment?.value?.reason : ' - '}`,
              );
            });
            console.groupEnd();

            if (hasNonLockFailures && globalToastRef.current) {
              globalToastRef.current.show({
                severity: 'error',
                summary: 'Case assignment',
                detail: 'Some documents failed to be assigned. Check the console for details.',
                sticky: false,
                life: 5000,
              });
            }
          }

          // Also when only some went through, so those show as assigned
          if (failedAssignments.length < results.length) {
            getMedicalRecordsListRequest();
          }
        });

        table.resetRowSelection();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [caseSelectorPendingRows, triggerPostDocumentAssignCase, getMedicalRecordsListRequest],
  );

  const columns = useMemo<ColumnDef<MedicalRecordsListTableRow, unknown>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <div className="px-1">
            <IndeterminateCheckbox
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: 'documentName',
        header: 'Document',
        cell: (info) => {
          const fileName = info.getValue() as string;
          const row = info.row.original;
          const fileSize = row.documentSize ? parseFloat(String(row.documentSize)).toFixed(2) : null;
          const pageCount = row.pageCount;

          if (!fileName) return '';

          const displayName = fileName.length > 24 ? `${fileName.slice(0, 24)}...` : fileName;

          return (
            <div className="flex items-center gap-2">
              <PdfIcon className="h-4 w-4 shrink-0" />
              <div className="flex flex-col">
                <span
                  className={classNames('block overflow-hidden font-semibold overflow-ellipsis whitespace-nowrap')}
                  title={fileName}
                >
                  {displayName}
                </span>
                <div className="flex items-center gap-2">
                  {pageCount !== null && pageCount !== undefined && pageCount > 0 && (
                    <span className="text-fletchers-gray-250 text-[12px]/[16px]">
                      {pageCount} {pageCount === 1 ? 'page' : 'pages'}
                    </span>
                  )}
                  {fileSize && <span className="text-fletchers-gray-250 text-[12px]/[16px]">{fileSize} MB</span>}
                </div>
              </div>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: 'uploadedBy',
        header: 'Uploaded by',
        cell: (info) => {
          const row = info.row.original;
          const userName = row.uploadedBy.userName || '';
          return (
            <div className="flex items-center gap-2">
              <UserAvatar name={userName} size={24} fontSize={10} />
              <div className="flex flex-col">
                <span
                  className={classNames('block overflow-hidden overflow-ellipsis whitespace-nowrap')}
                  title={userName}
                  style={{
                    maxWidth: row.uploadedAt
                      ? `${format(new Date(row.uploadedAt), 'HH:mm d MMM y').length * 7}px`
                      : '300px',
                  }}
                >
                  {userName}
                </span>
                <span className="text-fletchers-gray-250 text-[12px]/[16px] whitespace-nowrap">
                  {row.uploadedAt ? `${format(new Date(row.uploadedAt), 'HH:mm d MMM y')}` : ''}
                </span>
              </div>
            </div>
          );
        },
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        id: 'caseStatus',
        accessorKey: 'caseName',
        header: 'Case',
        cell: (info) => {
          const rowId = info.row.id;
          const row = info.row.original;
          const caseStatus = row.caseStatus || '';

          return <CaseCell row={row} rowId={rowId} caseStatus={caseStatus} onSelectCaseClick={handleSelectCaseClick} />;
        },
        enableColumnFilter: true,
        enableSorting: false,
      },
      {
        accessorKey: 'providers',
        header: 'Provider',
        cell: (info) => {
          const rowId = info.row.id;
          const row = info.row.original;
          const providerName = row.providerName || '';
          const providerType = row.providerType || '';

          return (
            <ProviderCell
              rowId={rowId}
              providerName={providerName}
              providerType={providerType}
              onSelectProviderClick={handleSelectProviderClick}
            />
          );
        },
        enableColumnFilter: true,
        enableSorting: false,
      },
      {
        accessorKey: 'documentStatus',
        header: 'Status',
        cell: (info) => {
          const stateValue = info.getValue() as string;
          if (!stateValue || !stateValue.length || typeof stateValue !== 'string') {
            return '';
          }

          const formattedStatus = formatStatus(stateValue);

          const isProcessing = DOCUMENT_PROCESSING_STATUSES.some(
            (status) => stateValue.toLowerCase() === status.toLowerCase(),
          );
          const isError = DOCUMENT_ERROR_STATUSES.some((status) => stateValue.toLowerCase() === status.toLowerCase());

          if (isError) {
            const isValidationError = stateValue.toLowerCase() === 'validation error';
            const hasFailureReason = info.row.original.documentProcessingFailureReason;

            if (isValidationError && hasFailureReason) {
              return (
                <StatusCell
                  formattedStatus={formattedStatus}
                  failureReason={info.row.original.documentProcessingFailureReason}
                />
              );
            }

            return (
              <div className="flex items-center gap-2">
                <YellowWarningIcon style={{ width: '16px', height: '16px' }} />
                <span className="text-fletchers-black-900 font-semibold">{formattedStatus}</span>
              </div>
            );
          }

          if (isProcessing) {
            return (
              <div className="flex items-center" style={{ gap: '8px' }}>
                <div style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <ProgressSpinner style={{ width: '16px', height: '16px' }} strokeWidth="4" animationDuration="1.5s" />
                </div>
                <span className="text-fletchers-black-900 font-semibold">{formattedStatus}</span>
              </div>
            );
          }

          return <span className="text-fletchers-black-900 font-semibold">{formattedStatus}</span>;
        },
        enableColumnFilter: true,
        enableSorting: false,
      },
      {
        accessorKey: 'checkedBy',
        header: 'Checked by',
        cell: (info) => {
          const row = info.row.original;
          const userName = row.checkedBy?.userName || '';
          const documentId = row.documentId;

          if (!userName) return <span className="text-fletchers-gray-250 italic">N/A</span>;

          const handleClick = () => {
            if (row.checkCompleted && documentId) {
              const buttonEvent = {
                currentTarget: { dataset: { gdprCheckCompletedDocumentId: documentId } },
              } as unknown as React.MouseEvent<HTMLButtonElement>;
              handleGdprCheckCompletedClick(buttonEvent);
            }
          };

          return (
            <div
              className={classNames(
                'flex items-center gap-2',
                row.checkCompleted && 'cursor-pointer transition-opacity hover:opacity-80',
              )}
              onClick={handleClick}
            >
              <UserAvatar name={userName} size={24} fontSize={10} />
              <div className="flex flex-col">
                <span
                  className={classNames('block overflow-hidden overflow-ellipsis whitespace-nowrap')}
                  title={userName}
                >
                  {userName}
                </span>
                {row.checkCompleted && (
                  <span className="text-fletchers-gray-250 text-[12px]/[16px] whitespace-nowrap">
                    {format(new Date(row.checkCompleted), 'HH:mm d MMM y')}
                  </span>
                )}
              </div>
            </div>
          );
        },
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        header: '',
        accessorKey: 'actions',
        cell: (info) => {
          const rowValue = info.row.original;
          const documentId = rowValue.documentId;
          const unassignedDocumentId = rowValue.unassignedDocumentId;
          return (
            <div className="flex flex-nowrap items-center justify-end gap-2 opacity-0 transition-opacity group-hover:opacity-100">
              {!rowValue.unassignedDocumentId && !rowValue.checkCompleted && (
                <>
                  {(rowValue.documentStatus === DocumentStatus.READY_FOR_GDPR_CHECK ||
                    rowValue.documentStatus === DocumentStatus.GDPR_CHECK_IN_PROGRESS) && (
                    <button
                      type="button"
                      className="border-fletchers-gray-110 hover:bg-fletchers-gray-110 flex h-8 shrink-0 cursor-pointer items-center justify-center rounded border bg-white transition-colors disabled:cursor-default disabled:opacity-50"
                      style={{ paddingLeft: '16px', paddingRight: '16px' }}
                      data-document-id={rowValue.documentId || ''}
                      data-check-started={!!rowValue.checkStarted}
                      disabled={!!rowValue.documentId && startingGdprCheckDocumentIds.includes(rowValue.documentId)}
                      onClick={handleGdprStartReviewClick}
                    >
                      <span className="text-fletchers-black-900 text-sm font-semibold whitespace-nowrap">
                        {rowValue.checkStarted ? 'Resume check' : 'Start check'}
                      </span>
                    </button>
                  )}
                </>
              )}
              {!['uploading', 'registered'].includes((rowValue.documentStatus || '').toLowerCase()) && (
                <button
                  type="button"
                  title="Download Document"
                  className="border-fletchers-gray-110 hover:bg-fletchers-gray-110 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded border bg-white transition-colors"
                  data-download-document-id={documentId}
                  data-download-unassigned-document-id={unassignedDocumentId}
                  data-download-document-filename={rowValue.documentName}
                  onClick={handleDownloadDocumentClick}
                >
                  <DownloadIcon className="text-fletchers-black-900" />
                </button>
              )}
              <DeleteDocumentButton
                title="Delete Document"
                isBlocked={isDocumentDeleteBlockedByCaseStatus(rowValue.caseStatus)}
                data-delete-document-id={documentId}
                data-delete-unassigned-document-id={unassignedDocumentId}
                onClick={handleDeleteDocumentClick}
              />
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    [
      handleDeleteDocumentClick,
      handleDownloadDocumentClick,
      handleGdprStartReviewClick,
      handleGdprCheckCompletedClick,
      handleSelectCaseClick,
      handleSelectProviderClick,
      startingGdprCheckDocumentIds,
    ],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),

    state: {
      pagination: medicalRecordsPagination,
      sorting: medicalRecordsSorting,
      columnFilters,
      rowSelection,
    },

    //** START SELECTION */
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    //** END SELECTION */

    //** START PAGINATION */
    rowCount: medicalRecordsRowCount,
    onPaginationChange: (updaterOrValue) => {
      // Check if the argument is a function (Updater) or a direct value (PaginationState)
      if (typeof updaterOrValue === 'function') {
        // If it's an updater function, call it with the current state to get the new state
        const newPaginationState = updaterOrValue(medicalRecordsPagination);
        dispatch(setMedicalRecordsPagination(newPaginationState));
      } else {
        // If it's a direct value, dispatch it directly
        dispatch(setMedicalRecordsPagination(updaterOrValue));
      }
    },
    manualPagination: true,
    //** END PAGINATION */

    //** START SORTING */
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        const newSorting = updaterOrValue(medicalRecordsSorting);
        dispatch(setMedicalRecordsSorting(newSorting));
      } else {
        dispatch(setMedicalRecordsSorting(updaterOrValue));
      }
      // Reset pagination to first page
      dispatch(
        setMedicalRecordsPagination({
          ...medicalRecordsPagination,
          pageIndex: 0,
        }),
      );
    },
    manualSorting: true,
    //** END SORTING */
  });

  return {
    table,
    filterOptions,
    selectedFilterOptions,
    selectedUploadDateRange,
    // Only while the current page has nothing loaded yet, so background refreshes (polling,
    // GDPR status checks) don't show the spinner over rows that are still usable.
    isTableDataLoading: getMedicalRecordsFetching && !medicalRecordsCurrentData,
    handleFilterChange,
    handleUploadedByFilterChange,
    // GDPR Check Summary Modal state
    gdprSummaryVisible,
    setGdprSummaryVisible,
    gdprSummaryData,
    gdprSummaryLoading,
    // Case Selector Popup state
    caseSelectorVisible,
    setCaseSelectorVisible,
    caseSelectorPosition,
    casesBasicList,
    handleCaseSelect,
    // Provider Selector Popup state
    providerSelectorVisible,
    setProviderSelectorVisible,
    providerSelectorPosition,
    caseProvidersList,
    handleProviderSelect,
  };
};
